import { useState } from 'react'
import { Head, Link, useForm } from '@inertiajs/react'
import { HomeMark } from '../../shared/HomeMark.jsx'
import { withSiteFooter } from '../../shared/SiteFooter.jsx'
import { Button } from '../../components/core/Button.jsx'
import { Divider } from '../../components/core/Divider.jsx'
import { Field } from '../../components/forms/Field.jsx'
import { Input } from '../../components/forms/Input.jsx'
import { Alert } from '../../components/feedback/Alert.jsx'

// GET /verify. An already-verified visitor is redirected away, so there is no
// "already confirmed" state. A successful confirmation has no screen either: it
// redirects to the dashboard, where the first collection is running.
// Both actions report failure (including the rate limit) as errors.email.

// `held_summary` and `held_reason` arrive written rather than being typed here,
// which is the rule every other Class 2 string on a signed-in page already
// follows. This page held its own copy of both, and so did both mailer parts —
// two sanctioned sentences in four places each, and `held_summary`'s had already
// drifted in the mailers. `Billing::Summary::HELD` and `::HELD_REASON` are the
// single sources and `billing_page_test` holds both against copy-rules.md.
//
// **They are two strings rather than one, which is why fixing the first did not
// fix the second.** `held_summary` was given a source on 2026-10-08 and the
// sentence immediately beneath it stayed a literal until 2026-10-09, because
// nothing compares a page against the authority. `copy-rules.md` sanctions them
// separately, the limit alone and then "with the reason, where there is room for
// it" — so they must stay separately quotable.
//
// **No fallback default on either, deliberately.** A default for a Class 2 string
// is a second copy, and a second copy is how a corrected sentence keeps rendering
// in its old form — the exact failure these props exist to end.
//
// **An account that signed up to buy a plan gets `purchase` instead**, 2026-10-27,
// and both held sentences arrive null: confirming its email opens checkout rather
// than starting a collection, so the held sentence would be false for it.
// `purchase.confirm_lead` and `.confirm_reason` say where the link goes, written
// once in `Billing::Purchase` for this page and the email alike.
export default function Verify({ email = '', held_summary = null, held_reason = null, purchase = null, errors = {} }) {
  const resend = useForm({})
  const correct = useForm({ email })
  const [sent, setSent] = useState(null)

  const unchanged = correct.data.email.trim().toLowerCase() === email.trim().toLowerCase()

  function sendAgain(e) {
    e.preventDefault()
    resend.post('/verify', { preserveScroll: true, onSuccess: () => setSent('again') })
  }

  function submitCorrection(e) {
    e.preventDefault()
    correct.patch('/verify', { preserveScroll: true, onSuccess: () => setSent('corrected') })
  }

  return (
    <>
      <Head title="Confirm your email" />
      <main className="mx-auto flex min-h-screen w-full max-w-md flex-col justify-center gap-8 px-6 py-16">
        <HomeMark />

        <div className="flex flex-col gap-3">
          <h1 className="page-title">
            {purchase ? 'Confirm your email to continue to checkout' : 'Confirm your email to start collecting'}
          </h1>
          <p className="text-[15px] text-[color:var(--text-body)]">{purchase ? purchase.confirm_lead : held_summary}</p>
          <p className="text-[13.5px] text-[color:var(--text-muted)]">{purchase ? purchase.confirm_reason : held_reason}</p>
        </div>

        <div className="flex flex-col gap-4">
          <p className="text-[15px] text-[color:var(--text-body)]">
            The link went to{' '}
            {/*
              Not mono, and not `tabular-nums`. Mono reads as code to be typed
              exactly; this is the address they just gave us. `tabular-nums`
              aligns digit columns, which an email address has no use for.
              Medium weight carries the emphasis instead, and the colour stays
              `--text-strong` — teal and amber both read as interactive here, and
              nothing about this is clickable. `break-all` stays: a long address
              must not push the column wider.
            */}
            <span className="font-medium text-[color:var(--text-strong)] break-all">{email}</span>, and
            it will be valid for two days.
          </p>

          {sent === 'again' && (
            <Alert tone="data" icon="mail">
              Another link is on its way to {email}.
            </Alert>
          )}
          {sent === 'corrected' && (
            <Alert tone="data" icon="mail">
              A new link is on its way to {email}. The previous one no longer works.
            </Alert>
          )}

          <form onSubmit={sendAgain}>
            <Button variant="secondary" size="lg" iconLeft="refresh-cw" as="button" loading={resend.processing}>
              Send it again
            </Button>
          </form>
        </div>

        <Divider />

        <form onSubmit={submitCorrection} className="flex flex-col gap-5">
          <div className="flex flex-col gap-1">
            <h2 className="text-[17px] font-semibold leading-snug">Fix an incorrect email address</h2>
            <p className="text-[13.5px] text-[color:var(--text-muted)]">
              Enter the correct address and a new link will be sent for confirmation. The previous
              link will stop working as soon as the new one is sent.
            </p>
          </div>

          <Field
            label="Email"
            htmlFor="email"
            error={errors.email}
          >
            <Input
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              value={correct.data.email}
              invalid={Boolean(errors.email)}
              onChange={(e) => correct.setData('email', e.target.value)}
            />
          </Field>

          <Button variant="primary" size="lg" block as="button" disabled={unchanged} loading={correct.processing}>
            Send to this address
          </Button>
        </form>

        {/*
          Re-applied on integration. The dashboard link earns its place: this page
          argues that nothing is locked, and a working way back proves it better
          than the sentence does. Right after signup it lands on brand setup,
          because /dashboard redirects when there is no business yet — correct,
          not a detour.

          **Sign out is deliberately not here.** The wrong-account case this page
          could strand someone in is already solved above, by correcting the
          address — and sign out now lives in the signed-in header, which is where
          the first one belonged rather than alone on a verification screen.
        */}
        {/*
          The escape hatch says what it costs, which it did not. "Carry on
          without confirming" offered a choice and hid the consequence; the
          second line is taken from what an unverified account may actually do,
          which is everything except spend — sign in, set up a business, see
          every screen. Only the first collection waits.
        */}
        <div className="flex flex-col gap-1">
          <Link
            href="/dashboard"
            className="text-[13.5px] text-[color:var(--text-muted)] underline underline-offset-4"
          >
            Carry on without confirming
          </Link>
          <p className="text-[13.5px] text-[color:var(--text-muted)]">
            {purchase
              ? 'You can look around the app. Checkout waits until your email is confirmed.'
              : 'You can set up your business and look around the app. Collection waits until your email is confirmed.'}
          </p>
        </div>
      </main>
    </>
  )
}

Verify.layout = withSiteFooter
